import React from 'react';
import ServiceSubpage, { Lead, SubHead, Included, Callout } from '../components/ServiceSubpage';

export default function ServiceAssessment() {
  return (
    <ServiceSubpage
      seo={{
        title: 'AI Readiness Assessment for Small Businesses & Nonprofits | Sagient Partners',
        description: 'An AI audit for small businesses and nonprofits that maps your workflows, surfaces top automation opportunities, and delivers a prioritized AI feasibility roadmap. One-time · 2–4 weeks · $3,000–$5,000.',
        path: '/services/ai-readiness-assessment',
      }}
      hero={{
        tier: 'Tier 1 · AI Readiness Assessment · One-time · 2-4 weeks · $3,000-$5,000',
        headline: 'AI Readiness Assessment for Small Businesses & Nonprofits',
        subhead: 'Find where AI workflow automation and small business AI solutions will actually pay off in your small business or nonprofit — before you spend a dollar on software. A structured audit of how work moves through your organization, ending in a prioritized, ROI-ranked roadmap you own.',
        meta: 'Best first step · Low-risk, high-insight',
        cta: 'Start With an Assessment →',
        ctaHeading: 'Not Sure Where to Start?',
        ctaSub: 'Most clients start here. It is low-risk, high-insight, and sets the foundation for everything that follows.',
      }}
      schema={{
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'Service',
            name: 'AI Readiness Assessment',
            serviceType: 'AI Consulting',
            provider: { '@type': 'Organization', name: 'Sagient Partners', url: 'https://sagientpartners.com' },
            description: 'A one-time engagement that audits your operations, identifies AI opportunities, and delivers a prioritized, ROI-ranked implementation roadmap.',
            areaServed: { '@type': 'Country', name: 'United States' },
            audience: [
              { '@type': 'Audience', audienceType: 'Small Business' },
              { '@type': 'Audience', audienceType: 'Nonprofit' },
            ],
            offers: { '@type': 'Offer', priceCurrency: 'USD', priceRange: '$3,000-$5,000', lowPrice: '3000', highPrice: '5000' },
          },
          {
            '@type': 'FAQPage',
            mainEntity: [
              { '@type': 'Question', name: 'Will this disrupt my team?', acceptedAnswer: { '@type': 'Answer', text: 'No. Most interviews take under an hour and we work around your schedule.' } },
              { '@type': 'Question', name: "Can my small business do an AI audit if we don't have clean data or an IT team?", acceptedAnswer: { '@type': 'Answer', text: "Yes. That's exactly what the Assessment sorts out, and part of the benefit of agentic AI." } },
              { '@type': 'Question', name: 'Do I have to buy anything after?', acceptedAnswer: { '@type': 'Answer', text: 'No. The roadmap stands on its own — many clients implement the quick wins themselves.' } },
            ],
          },
        ],
      }}
      faqs={[
        { q: 'Will this disrupt my team?', a: 'No. Most interviews take under an hour and we work around your schedule.' },
        { q: "Can my small business do an AI audit if we don’t have clean data or an IT team?", a: "Yes. That’s exactly what the Assessment sorts out, and part of the benefit of agentic AI." },
        { q: 'Do I have to buy anything after?', a: 'No. The roadmap stands on its own - many clients implement the quick wins themselves.' },
      ]}
      prevNext={[{ to: '/services/ai-strategy-implementation', label: 'Next: AI Strategy Implementation →' }]}
    >
      <section className="section" style={{ paddingTop: 32, paddingBottom: 32 }}>
        <div className="container">
          <p className="section-label">Why Small Businesses & Nonprofits Need an AI Feasibility Audit</p>
          <Lead>
            For most small businesses and nonprofits, time and capacity are the constraints on growth. It is the strategic
            work that never gets done because the operational work never ends. And the biggest time-consuming efforts are
            not always the obvious tasks. They hide in the workflows of intake steps that seem too minor to systematize,
            handoffs between people, and the follow-ups that depend on someone remembering until they quietly eat a lot of
            the week. That is why the first move isn&rsquo;t choosing an AI tool. It is getting clear on how work actually
            flows, so you can see where the hours really go.
          </Lead>

          <Callout label="Why It Matters Now">
            75% of small businesses are already experimenting with AI productivity tools — and among adopters, 91% report
            revenue gains and 86% see better margins. Nonprofits face the same opportunity: the same agentic AI tools
            that automate sales follow-up for a business automate donor outreach and grant vetting for a nonprofit. But
            most organizations are still one person prompting ChatGPT, one draft at a time. The ones seeing real
            operational benefit are documenting repeatable AI workflows that run without constant supervision. That
            starts with knowing which workflows are worth automating.
          </Callout>

          <SubHead>Think you are not ready for Agentic AI yet? That is probably not true.</SubHead>
          <Lead>
            Readiness isn&rsquo;t about perfect data, a modern technology, or an IT department. It is about having a real
            problem worth solving and the willingness to change a process. Organizations can automate meaningful workflows
            with little more than a spreadsheet, an inbox, and the right tools connected together. The Assessment
            determines if the problem is worth solving, and if the value is worth it. The assessment is completed in
            days - not months.
          </Lead>
        </div>
      </section>

      <section className="section section--alt" style={{ paddingTop: 32, paddingBottom: 32 }}>
        <div className="container" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 48 }}>
          <div>
            <p className="section-label">Our 2–4 Week Small Business & Nonprofit AI Workflow Audit</p>
            <Lead>
              Over 2 to 4 weeks we trace your core workflows end to end. For example, lead to quote to job to paid invoice
              or, for a nonprofit, first inquiry to enrolled or funded. We mark every point where work arrives (intake), is
              handed off, or stalls waiting on a free moment. We identify the repetitive, rules-governed steps eating more
              than a few hours a week. We look at data arriving and exiting at every point for availability, gaps, format,
              cleanness, and the opportunity to augment internal data with external data. Finally, we match the workflows
              against what agentic AI can genuinely do today and rank every opportunity by impact and effort.
            </Lead>
          </div>
          <div>
            <p className="section-label">What&rsquo;s Included</p>
            <Included items={[
              'An operations and workflow audit across your key functions',
              'An AI opportunity matrix that prioritizes every opportunity by impact and effort',
              'Tool and vendor evaluation with estimated cost matched to where your data already lives',
              'A written phased implementation timeline you own and can act on with or without us',
              'ROI analysis and quick-win identification so you see returns early',
            ]} />
          </div>
        </div>
      </section>

      <section className="section" style={{ paddingTop: 32, paddingBottom: 32 }}>
        <div className="container">
          <p className="section-label">What You Walk Away With</p>
          <Lead>
            A clear roadmap showing your top opportunities, what each is worth, what it takes to build, and the recommended
            order to do them in. Yours to keep with no lock-in.
          </Lead>
          <p style={{ fontSize: '0.95rem', color: '#0C447C', fontStyle: 'italic', marginTop: 8 }}>
            <strong>Ideal for:</strong> Owners, general managers, and executive directors of small businesses and nonprofits searching for practical AI consulting — who want a clear ROI analysis before buying any software or committing implementation budget.
          </p>
        </div>
      </section>
    </ServiceSubpage>
  );
}
